/*
 * ExperienceReportPage — routed dashboard surface for the v2.7
 * auto-generated Experience Report.
 *
 * The report used to be a `fixed inset-0` overlay inside OrbitScopeChat,
 * which made it un-bookmarkable and stripped the dashboard sidebar.
 * This page mounts inside LayoutWithAuth so the sidebar wraps it like
 * /my-business and /orbit/scope, and gives the report a stable URL the
 * MyBusiness AutomatedLane can deep-link into.
 *
 * Reads `:engagementId` from the route. Renders ExperienceReportPanel
 * in `variant="page"` mode (no close-X, no fixed positioning) inside a
 * max-width container with brand chrome. On forward-path pick, routes
 * the user to the right next-step surface based on the pick:
 *   • build / both → /my-business (lane card flips to "Drafting PRD")
 *   • training → /my-business (training plan summary appears)
 *   • scope-more → /orbit/scope/:engagementId (return to the rail)
 */

import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

import { ExperienceReportPanel } from '../orbit/scope/ExperienceReportPanel';
import type { ExperienceReportForwardPath } from '../orbit/lib/scopeApi';


const ExperienceReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { engagementId } = useParams<{ engagementId: string }>();

  const onPathPicked = useCallback((_path: ExperienceReportForwardPath['id']) => {
    // Forward paths (build / training / both) all route to /my-business —
    // the AutomatedLane card picks up the new routing_decision.forward_path
    // and surfaces the next state (Drafting PRD, opening workspace, etc.).
    navigate('/my-business');
  }, [navigate]);

  if (!engagementId) {
    return (
      <div className="mx-auto max-w-[1100px] px-6 py-12 font-sans">
        <p className="text-sm text-gray-700">No engagement specified.</p>
        <button
          type="button"
          onClick={() => navigate('/my-business')}
          className="mt-3 inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700"
        >
          <ChevronLeft size={14} />
          Back to my business
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-4 font-sans">
      <button
        type="button"
        onClick={() => navigate('/my-business')}
        className="mb-3 inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-brand-600"
      >
        <ChevronLeft size={14} />
        My business
      </button>
      <div className="rounded-xl bg-white ring-1 ring-gray-200/60">
        <ExperienceReportPanel
          engagementId={engagementId}
          variant="page"
          onPathPicked={onPathPicked}
        />
      </div>
    </div>
  );
};

export default ExperienceReportPage;
